
import Component from "vue-class-component";
import Vue from "vue";
import {
  ApplicationActions,
  ApplicationHistoryEventModel,
  ApplicationsClient,
  ApplicationViewModel,
  PersonalInfoClient,
  PersonalInfoOptions,
} from "@/api-client";
import CreateUUID from "@/guid";
import { Prop } from "vue-property-decorator";
import Loader from "@/features/shared/components/Loader";
import NavApplication from "@/features/applications/components/NavApplication.vue";
import {NavigationGuardNext, Route} from "vue-router/types/router";

interface Props {
    preLoadedApplication: ApplicationViewModel;
    preLoadedActions: ApplicationActions;
    preLoadedPersonalInfoOptions: PersonalInfoOptions;
}

@Component({
    components: {
        NavApplication,
    },
    beforeRouteEnter(to, from, next: NavigationGuardNext<ApplicationRoot>) {
        const props = to.meta!.props as Props;
        const applicationId = to.params.applicationId;
        Promise.all([
            new ApplicationsClient().find(applicationId),
            new ApplicationsClient().availableActions(applicationId)
        ]).then(result => {
            props.preLoadedApplication = result[0];
            props.preLoadedActions = result[1];

            // not the best solution, as you have to wait for first values before this one will get queried.
            // better would be to already give seafarerId in params, but for this the different
            // application list views etc need to have seafarerId available too, which needs extensive backend changes.
            new PersonalInfoClient().options(props.preLoadedApplication.seafarerId).then(result => {
              props.preLoadedPersonalInfoOptions = result;

              next();
            }).catch(e => next(e));
        }).catch(e => next(e));
}
})
export default class ApplicationRoot extends Vue implements Props {
  @Prop({required: true})
  preLoadedApplication!: ApplicationViewModel;
  @Prop({required: true})
  preLoadedActions!: ApplicationActions;
  @Prop({required:true})
  preLoadedPersonalInfoOptions!: PersonalInfoOptions;

  application: ApplicationViewModel = this.preLoadedApplication;
  personalInfoOptions: PersonalInfoOptions = this.preLoadedPersonalInfoOptions;
  actions: ApplicationActions = this.preLoadedActions;

  innerRouterKey = CreateUUID();

  historyLoader: Loader = new Loader();
  historyEvents: ApplicationHistoryEventModel[] = [];

  isOnMobile = false;
  isSideMenuOpen = false;

  get isSideMenuHidden() {
    return !this.isSideMenuOpen && this.isOnMobile;
  }

  created() {
    this.onResize();
    this.$watch(() => this.$route, () => this.hideSideMenu());
    this.$watch(() => this.$route, _ => this.innerRouterKey = CreateUUID());
    window.addEventListener("resize", this.onResize);
    this.refreshHistory();
  }

  refreshHistory() {
    new ApplicationsClient()
        .setLoader(this.historyLoader)
        .history(this.application.id)
        .then(result => this.historyEvents = result);
  }

  navigateToRequirementOverview(applicationId: string, requirementId: string) {
    this.$router.push({name: 'RequirementOverview', params: {applicationId: applicationId, requirementId: requirementId, successMessage: "true"}});
  }

  navigateToApplicationOverView(applicationId: string) {
    this.$router.push({name: 'ApplicationOverview', params: {applicationId: applicationId, successMessage: "true"}});
  }

  async refresh() {
    const applicationId = this.$route.params.applicationId;
    this.actions = await new ApplicationsClient().availableActions(
            applicationId
    );
    this.application = await new ApplicationsClient().find(applicationId);
    this.refreshHistory();

    const currentRequirementId = this.$route.params.requirementId;

    // only redirect if current requirement has been decided on.
    if (this.application.requirementFulfillments.find(r => r.requirementId === currentRequirementId)?.requirementDecision) {

      // if there is no undecided requirementFulfillment, all have been decided on, therefore redirect to previous page.
      // otherewise redirect to the first undecided requirementFulfillment.

      const currentIndex = this.application.requirementFulfillments.findIndex(r => r.requirementId === currentRequirementId);

      // only consider requirements that come after the current one, as officials dont have to decide for every requirement.
      const undecidedIndex = this.application.requirementFulfillments.findIndex((r, index) => r.requirementDecision === null && index > currentIndex);

      // if currentindex is last that means weve reached the end and need to go to the beginning.
      // if we havent reached the end, but all requirements have already been accepted, we also need to skip to the beginning, as theres nothing left to do.
      if (currentIndex === this.application.requirementFulfillments.length - 1 || undecidedIndex === -1) {
        this.navigateToApplicationOverView(applicationId);
      }
      else {
        const nextRequirementId = this.application.requirementFulfillments[undecidedIndex].requirementId;
        this.navigateToRequirementOverview(applicationId, nextRequirementId);
      }
    }

    this.innerRouterKey = CreateUUID(); // NOTE: This HAS to be last statement. If "application" or "actions" update after this then the change might not be displayed if some components are re-used by Vue.
  }

  onResize() {
    // TODO(KK): Is it possible to use a shared constant here?
    if (window.innerWidth <= 768) { // This size breakpoint has to match with grid md breakpoint
        this.isOnMobile = true;
    } else {
      this.isOnMobile = false;
      this.isSideMenuOpen = false;
    }
  }

  toggleSideMenu() {
      this.isSideMenuOpen = !this.isSideMenuOpen;
  }

  hideSideMenu() {
    this.isSideMenuOpen = false;
  }
}
